import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api, { fmtMoney, formatApiError } from "@/lib/api";
import { usePersistedClientId } from "@/lib/clientSelection";
import { fetchAccountingCfdiXmlBlob, fetchCfdiPdfBlob } from "@/lib/exportDirectorioApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  CloudDownload,
  FileCode2,
  FileText,
  Loader2,
  RefreshCw,
  ShieldCheck,
  ShieldAlert,
} from "lucide-react";
import { toast } from "sonner";
import { VISOR_HUB_PERIOD_EVENT } from "@/lib/visorHubPeriodEvents";
import { useAuth } from "@/lib/auth";
import { usePlatformDesigner } from "@/lib/usePlatformDesigner";
import { useSatDownloadJobsOptional } from "@/lib/satDownloadJobs";
import AccountingZipExportPanel from "@/components/accounting/AccountingZipExportPanel";
import { formatMissingMonthsPhrase } from "@/lib/formatMissingMonths";

const PAGE_SIZES = [25, 50, 100];

function syncQueueStatusLabel(estado) {
  const st = (estado || "").trim().toLowerCase();
  if (st === "pendiente") return "En cola (pendiente)";
  if (st === "procesando") return "Descargando ahora (procesando)";
  if (st === "completado") return "Ciclo completado";
  if (st === "error") return "Error en cola";
  return "Sincronización activa — esperando turno";
}

function describeDesignerSatQueueMessage(client, xmlRecovery) {
  const syncOn = Boolean(client?.modulo_descarga_activo || xmlRecovery?.sync_auto_active);
  if (!syncOn) {
    return "Sincronización automática desactivada: no se encolará la descarga del SAT hasta activarla y guardar la e.firma.";
  }

  const estado = (client?.estado_sincronizacion || "").trim().toLowerCase();
  const targetFrom = (client?.sat_sync_target_from || "").slice(0, 10);
  const targetTo = (client?.sat_sync_target_to || "").slice(0, 10);
  const period =
    targetFrom && targetTo ? ` Periodo en cola: ${targetFrom} — ${targetTo}.` : "";
  const priority =
    client?.sat_sync_priority != null && client.sat_sync_priority !== ""
      ? ` Prioridad cola: ${client.sat_sync_priority}.`
      : "";

  if (xmlRecovery?.auto_healing_queued || xmlRecovery?.healing_started) {
    return `Healing XML encolado en la cola SAT del robot.${period}${priority} No hace falta forzar descarga manual.`;
  }

  if (estado === "pendiente") {
    return `${syncQueueStatusLabel(estado)}.${period}${priority} El scheduler procesará en segundo plano.`;
  }
  if (estado === "procesando") {
    return `${syncQueueStatusLabel(estado)}.${period} Puede cerrar esta ventana.`;
  }

  return `Los meses con XML faltante se encolan automáticamente al reconciliar o en la próxima pasada del robot.${period}`;
}

function computePendingIntegrateCount(summary, stats, auditStatus) {
  const fromAudit = Number(auditStatus?.xml_pending_integration);
  if (Number.isFinite(fromAudit) && fromAudit >= 0) {
    return fromAudit;
  }
  const fromStats = Number(stats?.xml_pending_integration);
  if (Number.isFinite(fromStats) && fromStats >= 0) {
    return fromStats;
  }
  return Math.max(Number(summary?.missing) || 0, Number(summary?.healing) || 0);
}

function folioDisplay(row) {
  const s = [row?.serie, row?.folio].filter(Boolean).join("-");
  return s || "—";
}

function tipoComprobanteLabel(tipo) {
  const code = (tipo || "").trim().toUpperCase();
  const map = { I: "Ingreso", E: "Egreso", N: "Nómina", P: "Pago" };
  return map[code] || code || "—";
}

function formatFecha(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("es-MX", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  } catch {
    return String(iso).slice(0, 10);
  }
}

function inferRfcRoleFromDireccion(direccion) {
  const d = (direccion || "").trim().toLowerCase();
  if (d === "emitido") return "receptor";
  if (d === "recibido") return "emisor";
  return "contraparte";
}

function looksLikeRfcTerm(term) {
  const t = (term || "").trim().toUpperCase().replace(/\s/g, "");
  if (t.length < 10) return false;
  return /^[A-ZÑ&]{3,4}\d{6}[A-Z0-9]{3}$/.test(t) || /^[A-ZÑ&]{3,4}\d{4,6}[A-Z0-9]{0,3}$/.test(t);
}

/** RFC exacto (rol inferido por dirección) o texto libre (nombre, UUID, folio…). */
function resolveAccountingSearch(term, direccion, clientRfc) {
  const raw = (term || "").trim();
  if (!raw) return {};
  const normalized = raw.toUpperCase().replace(/\s/g, "");
  const cr = (clientRfc || "").trim().toUpperCase();

  if (looksLikeRfcTerm(raw) || (normalized.length >= 12 && /^[A-Z0-9Ñ&]+$/.test(normalized))) {
    const rfc = normalized;
    if (cr && rfc === cr) {
      return { rfc, rfc_role: "contribuyente" };
    }
    return { rfc, rfc_role: inferRfcRoleFromDireccion(direccion) };
  }

  if (raw.length >= 2) {
    return { q: raw };
  }
  return {};
}

function EstadoBadge({ estado, kind }) {
  const cancelado = kind === "cancelado" || (estado || "").toLowerCase() === "cancelado";
  return (
    <Badge
      variant="outline"
      className={
        cancelado
          ? "border-red-200 bg-red-50 text-red-800"
          : "border-emerald-200 bg-emerald-50 text-emerald-800"
      }
    >
      {cancelado ? "Cancelado" : estado || "Vigente"}
    </Badge>
  );
}

function IntegrityStatusIcon({ status, title }) {
  const st = (status || "pending_check").toLowerCase();
  if (st === "verified") {
    return (
      <span className="inline-flex items-center gap-1 text-emerald-700" title={title || "Verificado"}>
        <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden />
        <span className="sr-only">Verificado</span>
      </span>
    );
  }
  if (st === "missing") {
    return (
      <span className="inline-flex items-center gap-1 text-red-700" title={title || "Archivo faltante"}>
        <ShieldAlert className="h-4 w-4 shrink-0" aria-hidden />
        <span className="sr-only">Faltante</span>
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1 text-amber-700"
      title={title || "Pendiente de verificación"}
    >
      <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden />
      <span className="sr-only">Pendiente</span>
    </span>
  );
}

function FuenteBadge({ label, code }) {
  const auto = code === "automatico";
  return (
    <Badge
      variant="secondary"
      className={
        auto
          ? "bg-sky-50 text-sky-900 border border-sky-200"
          : "bg-amber-50 text-amber-900 border border-amber-200"
      }
      title={auto ? "Descarga SAT / sincronización automática" : "Carga manual, carpeta u otro medio"}
    >
      {label}
    </Badge>
  );
}

function CredentialAuthBanner({ credentialHealth }) {
  if (!credentialHealth?.downloads_paused && !credentialHealth?.blocking_message) {
    return null;
  }
  return (
    <div
      className="rounded-md border border-red-300 bg-red-50 px-4 py-3"
      data-testid="accounting-credential-block"
      role="alert"
    >
      <div className="flex items-start gap-2">
        <AlertTriangle className="h-5 w-5 text-red-700 shrink-0 mt-0.5" aria-hidden />
        <div>
          <p className="text-sm font-semibold text-red-950 m-0">Credenciales SAT</p>
          <p className="text-xs mt-1 m-0 leading-relaxed text-red-900">
            {credentialHealth.blocking_message ||
              "Error de autenticación con el SAT. Por favor, actualiza tu e.firma para continuar la recuperación de periodos pendientes."}
          </p>
          {credentialHealth.resume_from_date ? (
            <p className="text-xs mt-1 m-0 text-red-800">
              Tras renovar la FIEL, el motor fiscal reanudará desde{' '}
              <span className="font-mono">{String(credentialHealth.resume_from_date).slice(0, 10)}</span>.
            </p>
          ) : null}
          <p className="text-xs mt-2 m-0">
            <Link to="/descarga-automatica-sat" className="text-red-800 underline font-medium">
              Ir a configuración de e.firma →
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

function integrityWindowRangeLabel(dashboard) {
  const from = dashboard?.window_start?.slice(0, 10);
  const to = dashboard?.window_end?.slice(0, 10);
  if (!from || !to) return null;
  return `${formatFecha(from)} al ${formatFecha(to)}`;
}

function integrityBannerCopy(dashboard) {
  const st = dashboard?.integrity_status;
  const range = integrityWindowRangeLabel(dashboard);
  const missing = dashboard?.missing_months || [];
  const gapPreview = missing
    .slice(0, 4)
    .map((m) => m.label || m.month)
    .join(", ");

  if (st === "in_progress") {
    return {
      title: "Actualizando información",
      detail: range
        ? `Descargando comprobantes del SAT (${range}). En breve se actualizará esta pantalla.`
        : "Descargando comprobantes del SAT. En breve se actualizará esta pantalla.",
    };
  }
  if (st === "complete") {
    return {
      title: "Datos actualizados",
      detail: range ? `Información del ${range}.` : "La base de datos está al día para este cliente.",
    };
  }
  if (st === "gaps") {
    return {
      title: "Histórico incompleto",
      detail: gapPreview
        ? `Faltan meses por integrar (${gapPreview}${missing.length > 4 ? "…" : ""}). El sistema los completará en segundo plano.`
        : range
          ? `Faltan meses por integrar en el periodo del ${range}.`
          : "Faltan meses por integrar. El sistema los completará en segundo plano.",
    };
  }
  return {
    title: "Sin comprobantes",
    detail: range
      ? `No hay CFDI almacenados del ${range}.`
      : "Inicie una descarga SAT o cargue XML manualmente.",
  };
}

function IntegrityBanner({ dashboard, xmlPendingCount = 0 }) {
  if (!dashboard) return null;
  const st = dashboard.integrity_status;
  const copy = integrityBannerCopy(dashboard);
  const range = integrityWindowRangeLabel(dashboard);
  const pendingXml = Math.max(0, Number(xmlPendingCount) || 0);
  const completeButXmlPending = st === "complete" && pendingXml > 0;
  const displayCopy = completeButXmlPending
    ? {
        title: "Inventario cargado — XML en integración",
        detail: range
          ? `Hay comprobantes registrados del ${range}, pero ${pendingXml.toLocaleString("es-MX")} aún sin archivo XML en su copia. Revise el aviso inferior; el sistema puede completarlos en segundo plano.`
          : `${pendingXml.toLocaleString("es-MX")} comprobante(s) aún sin XML en su copia local.`,
      }
    : copy;
  const styles = completeButXmlPending
    ? "border-sky-200 bg-sky-50 text-sky-950"
    : st === "in_progress"
      ? "border-sky-200 bg-sky-50 text-sky-950"
      : st === "complete"
        ? "border-emerald-200 bg-emerald-50 text-emerald-950"
        : st === "gaps"
          ? "border-amber-200 bg-amber-50 text-amber-950"
          : "border-slate-200 bg-slate-50 text-slate-800";
  const Icon = completeButXmlPending
    ? Loader2
    : st === "in_progress"
      ? CloudDownload
      : st === "gaps"
        ? AlertTriangle
        : ShieldCheck;
  return (
    <div className={`rounded-md border px-4 py-3 ${styles}`} data-testid="accounting-integrity-banner">
      <div className="flex items-start gap-2">
        <Icon
          className={`h-5 w-5 shrink-0 mt-0.5 ${completeButXmlPending ? "animate-spin text-sky-700" : ""}`}
          aria-hidden
        />
        <div>
          <p className="text-sm font-semibold m-0">{displayCopy.title}</p>
          <p className="text-xs mt-1 m-0 leading-relaxed">{displayCopy.detail}</p>
          {dashboard.rfc_filter ? (
            <p className="text-[11px] mt-1 m-0 text-slate-600">
              Filtro activo: <span className="font-mono">{dashboard.rfc_filter}</span>
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function HistoricalLoadSummary({ stats }) {
  if (!stats?.years?.length) {
    return <p className="text-xs text-slate-500 m-0">Sin datos de carga histórica.</p>;
  }
  return (
    <div className="space-y-3" data-testid="accounting-historical-load-summary">
      <div className="overflow-x-auto rounded-sm border border-slate-200">
        <table className="w-full text-xs">
          <thead className="bg-slate-50">
            <tr>
              <th className="text-left px-3 py-2 font-semibold text-slate-600">Año</th>
              <th className="text-right px-3 py-2 font-semibold text-slate-600">Documentos</th>
              <th className="text-left px-3 py-2 font-semibold text-slate-600">Estado</th>
              <th className="px-3 py-2 w-[40%]">Progreso</th>
            </tr>
          </thead>
          <tbody>
            {stats.years.map((row) => (
              <tr key={row.year} className="border-t border-slate-100">
                <td className="px-3 py-2 font-mono">{row.year}</td>
                <td className="px-3 py-2 text-right tabular-nums font-medium">
                  {row.count.toLocaleString("es-MX")}
                </td>
                <td className="px-3 py-2">
                  {row.synchronized ? (
                    <span className="text-emerald-700 font-medium">Sincronizado</span>
                  ) : (
                    <span className="text-amber-800 font-medium">Periodo no sincronizado</span>
                  )}
                </td>
                <td className="px-3 py-2">
                  <div
                    className="h-4 bg-slate-100 rounded-sm overflow-hidden"
                    title={
                      row.synchronized
                        ? "Periodo sincronizado en la ventana histórica"
                        : "Periodo sin datos en la ventana histórica"
                    }
                  >
                    <div
                      className={`h-full rounded-sm ${row.synchronized ? "bg-[#0F2D5C]/85" : "bg-amber-200"}`}
                      style={{
                        width: `${
                          row.synchronized
                            ? 100
                            : typeof row.progress_percent === "number"
                              ? row.progress_percent
                              : 2
                        }%`,
                      }}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[10px] text-slate-500 m-0">
        Ventana {stats.window_start?.slice(0, 10)} — {stats.window_end?.slice(0, 10)}
        {stats.window_scope_label ? ` (${stats.window_scope_label})` : ""} · total{" "}
        {stats.total_cfdis?.toLocaleString("es-MX")} CFDI (manual + automático)
      </p>
    </div>
  );
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Visor de contabilidad — listado unificado, integridad histórica (5 años) y aislamiento por cliente.
 */
export default function AccountingViewer({
  clients: clientsProp = null,
  hubEmbed = false,
  showXmlRecoveryUi = false,
}) {
  const { user } = useAuth();
  const isPlatformDesigner = usePlatformDesigner();
  const showTechnicalSyncUi = Boolean(isPlatformDesigner && !hubEmbed);

  const [clients, setClients] = useState(clientsProp || []);
  const [clientId, setClientId] = usePersistedClientId(clients);
  const [stats, setStats] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, total_pages: 1, limit: 50, skip: 0 });
  const [loading, setLoading] = useState(false);
  const [busyXml, setBusyXml] = useState("");
  const [busyPdf, setBusyPdf] = useState("");
  const [completingMonth, setCompletingMonth] = useState("");
  const [integrityRunning, setIntegrityRunning] = useState(false);
  const [integrityMessage, setIntegrityMessage] = useState("");
  const [integritySummary, setIntegritySummary] = useState(null);
  const [xmlIntegrity, setXmlIntegrity] = useState(null);
  const [xmlRecovery, setXmlRecovery] = useState(null);
  const [recoveryLogs, setRecoveryLogs] = useState([]);
  const [auditBusy, setAuditBusy] = useState(false);
  const [reconcileBusy, setReconcileBusy] = useState(false);
  const [forceAuditBusy, setForceAuditBusy] = useState(false);
  const [recoveryPhase, setRecoveryPhase] = useState("idle");
  const recoveryJobIdsRef = useRef([]);
  const runReconcileXmlRef = useRef(null);
  const satJobs = useSatDownloadJobsOptional();

  const [searchTerm, setSearchTerm] = useState("");
  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState("");
  const [tipo, setTipo] = useState("all");
  const [estatus, setEstatus] = useState("todos");
  const [direccion, setDireccion] = useState("all");
  const [appliedFilters, setAppliedFilters] = useState({
    searchTerm: "",
    fechaDesde: "",
    fechaHasta: "",
    tipo: "all",
    estatus: "todos",
    direccion: "all",
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [periodHint, setPeriodHint] = useState("");

  const relatedRfcs = useMemo(
    () => dashboard?.related_rfcs || [],
    [dashboard?.related_rfcs],
  );

  const refreshClients = useCallback(async () => {
    if (clientsProp) {
      setClients(clientsProp);
      return;
    }
    try {
      const { data } = await api.get("/clients");
      setClients(Array.isArray(data) ? data : []);
    } catch {
      setClients([]);
    }
  }, [clientsProp]);

  useEffect(() => {
    refreshClients();
  }, [refreshClients]);

  useEffect(() => {
    setSearchTerm("");
    setPage(1);
    setXmlRecovery(null);
  }, [clientId]);

  const pollAuditStatus = useCallback(async () => {
    if (!clientId) return null;
    try {
      const { data } = await api.get("/sat/cfdi/audit/status", { params: { client_id: clientId } });
      const st = data?.status || "idle";
      setIntegrityRunning(st === "running");
      setIntegrityMessage(data?.message || (st === "running" ? "Verificando integridad…" : ""));
      if (data?.summary) {
        setIntegritySummary(data.summary);
      } else if (data?.audit?.payload?.summary) {
        setIntegritySummary(data.audit.payload.summary);
      }
      setRecoveryLogs(data?.recovery_logs || []);
      if (data?.xml_integrity) {
        setXmlIntegrity(data.xml_integrity);
      }
      return data;
    } catch {
      return null;
    }
  }, [clientId]);

  const runStorageAudit = useCallback(
    async (fullClient = false) => {
      if (!clientId) return;
      setAuditBusy(true);
      try {
        const { data } = await api.post("/sat/cfdi/audit", {
          client_id: clientId,
          full_client: fullClient,
          trigger_healing: true,
        });
        setIntegrityRunning(data?.status === "running");
        setIntegrityMessage(data?.message || "Verificando integridad…");
        toast.info(fullClient ? "Auditoría completa iniciada" : "Verificación de integridad iniciada");
      } catch (err) {
        toast.error(formatApiError(err, "No se pudo iniciar la auditoría"));
      } finally {
        setAuditBusy(false);
      }
    },
    [clientId],
  );

  const runForceHealthAudit = useCallback(
    async (fullScan = false) => {
      if (!clientId) return;
      setForceAuditBusy(true);
      try {
        const { data } = await api.post("/internal/audit", {
          module_name: "cfdi",
          client_id: clientId,
          full_scan: fullScan,
          trigger_healing: true,
        });
        setIntegrityRunning(data?.status === "running");
        setIntegrityMessage(data?.message || "Verificando integridad…");
        toast.info("Auditoría de salud iniciada (Health Manager)");
      } catch (err) {
        toast.error(formatApiError(err, "No se pudo forzar la auditoría"));
      } finally {
        setForceAuditBusy(false);
      }
    },
    [clientId],
  );

  const activeClient = useMemo(
    () => clients.find((c) => c.id === clientId) || null,
    [clients, clientId],
  );

  const buildRfcParams = useCallback(
    (filters) => {
      const resolved = resolveAccountingSearch(
        filters.searchTerm,
        filters.direccion,
        activeClient?.rfc,
      );
      const params = {};
      if (resolved.rfc) {
        params.rfc = resolved.rfc;
        params.rfc_role = resolved.rfc_role;
      }
      return params;
    },
    [activeClient?.rfc],
  );

  const loadStats = useCallback(async () => {
    if (!clientId) {
      setStats(null);
      return;
    }
    setStatsLoading(true);
    try {
      const params = { client_id: clientId, ...buildRfcParams(appliedFilters) };
      const { data } = await api.get("/sat/cfdi/stats", { params });
      setStats(data);
      if (data?.xml_integrity) {
        setXmlIntegrity(data.xml_integrity);
      }
    } catch (err) {
      toast.error(formatApiError(err, "No se pudo cargar el resumen histórico"));
      setStats(null);
    } finally {
      setStatsLoading(false);
    }
  }, [clientId, appliedFilters, buildRfcParams]);

  const loadDashboard = useCallback(async () => {
    if (!clientId) {
      setDashboard(null);
      return;
    }
    setDashboardLoading(true);
    try {
      const params = { client_id: clientId, ...buildRfcParams(appliedFilters) };
      const { data } = await api.get("/sat/cfdi/dashboard", { params });
      setDashboard(data);
    } catch (err) {
      toast.error(formatApiError(err, "No se pudo cargar el resumen histórico"));
      setDashboard(null);
    } finally {
      setDashboardLoading(false);
    }
  }, [clientId, appliedFilters, buildRfcParams]);

  const loadList = useCallback(async () => {
    if (!clientId) {
      setItems([]);
      setMeta({ total: 0, page: 1, total_pages: 1, limit: pageSize, skip: 0 });
      return;
    }
    setLoading(true);
    try {
      const skip = (page - 1) * pageSize;
      const params = {
        client_id: clientId,
        skip,
        limit: pageSize,
        estatus: appliedFilters.estatus,
        ...buildRfcParams(appliedFilters),
      };
      if (appliedFilters.fechaDesde) params.fecha_desde = appliedFilters.fechaDesde;
      if (appliedFilters.fechaHasta) params.fecha_hasta = appliedFilters.fechaHasta;
      if (appliedFilters.tipo && appliedFilters.tipo !== "all") params.tipo = appliedFilters.tipo;
      if (appliedFilters.direccion && appliedFilters.direccion !== "all") {
        params.direccion = appliedFilters.direccion;
      }
      const searchResolved = resolveAccountingSearch(
        appliedFilters.searchTerm,
        appliedFilters.direccion,
        activeClient?.rfc,
      );
      if (searchResolved.q) {
        params.q = searchResolved.q;
      }

      const { data } = await api.get("/sat/cfdi/list", { params });
      setItems(data?.items || []);
      setMeta({
        total: data?.total ?? 0,
        page: data?.page ?? 1,
        total_pages: data?.total_pages ?? 1,
        limit: data?.limit ?? pageSize,
        skip: data?.skip ?? skip,
      });
    } catch (err) {
      toast.error(formatApiError(err, "No se pudo cargar el listado de CFDI"));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [clientId, page, pageSize, appliedFilters, activeClient?.rfc, buildRfcParams]);

  useEffect(() => {
    if (hubEmbed) {
      loadDashboard();
      return;
    }
    loadStats();
    loadDashboard();
  }, [hubEmbed, loadStats, loadDashboard]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const applyFilters = () => {
    setPage(1);
    setAppliedFilters({
      searchTerm,
      fechaDesde,
      fechaHasta,
      tipo,
      estatus,
      direccion,
    });
  };

  const refreshAll = () => {
    loadStats();
    loadDashboard();
    loadList();
  };

  const finishRecoverySuccess = useCallback(async () => {
    await pollAuditStatus();
    setRecoveryPhase("success");
    toast.success("Proceso terminado con éxito");
    window.setTimeout(() => setRecoveryPhase("idle"), 10000);
  }, [pollAuditStatus]);

  const applyXmlRecoveryResult = useCallback((data) => {
    const pendingAfter = Number(data?.xml_integrity?.pending_integration ?? data?.xml_pending_integration);
    setXmlRecovery(
      Number.isFinite(pendingAfter) && pendingAfter <= 0
        ? { ...data, still_missing: 0, missing_months: [] }
        : data,
    );
    if (data?.xml_integrity) {
      setXmlIntegrity(data.xml_integrity);
    }
  }, []);

  const runReconcileXml = useCallback(
    async ({ silent = false, triggerHealing = false } = {}) => {
      if (!clientId) return null;
      setReconcileBusy(true);
      if (!silent) {
        setRecoveryPhase("in_progress");
        toast.info(
          triggerHealing
            ? "Recuperando XML y, si hace falta, encolando descarga SAT…"
            : "En proceso de recuperación",
        );
      }
      try {
        const { data } = await api.post("/sat/cfdi/reconcile-xml", {
          client_id: clientId,
          trigger_healing: Boolean(triggerHealing),
        });
        applyXmlRecoveryResult(data);
        if (data?.sat_healing_user_requested || data?.healing_started) {
          await refreshClients();
        }
        await pollAuditStatus();
        if (!silent) {
          const jobIds = Array.isArray(data?.job_ids) ? data.job_ids.filter(Boolean) : [];
          recoveryJobIdsRef.current = jobIds;
          if (jobIds.length && satJobs?.registerJob) {
            const clientLabel = activeClient?.razon_social || activeClient?.rfc || "";
            jobIds.forEach((jobId) => {
              satJobs.registerJob({
                jobId,
                clientId,
                clientLabel,
                authLabel: "Recuperación XML",
              });
            });
          }
          if (data?.healing_started && jobIds.length) {
            toast.info(data.message || "Descarga SAT encolada en segundo plano");
            finishRecoverySuccess();
          } else {
            finishRecoverySuccess();
          }
        } else if (data?.reconciled > 0) {
          toast.success(data.message || `${data.reconciled} XML recuperados desde la base de datos`);
        }
        loadStats();
        loadDashboard();
        loadList();
        return data;
      } catch (err) {
        if (!silent) {
          setRecoveryPhase("idle");
          recoveryJobIdsRef.current = [];
        }
        toast.error(formatApiError(err, "No se pudo recuperar XML desde la base de datos"));
        return null;
      } finally {
        setReconcileBusy(false);
      }
    },
    [
      clientId,
      pollAuditStatus,
      loadStats,
      loadDashboard,
      loadList,
      satJobs,
      activeClient,
      finishRecoverySuccess,
      applyXmlRecoveryResult,
      refreshClients,
    ],
  );

  runReconcileXmlRef.current = runReconcileXml;

  useEffect(() => {
    if (hubEmbed || !clientId || !showXmlRecoveryUi) {
      if (!clientId) {
        setIntegrityRunning(false);
        setIntegritySummary(null);
        setRecoveryLogs([]);
        setRecoveryPhase("idle");
        recoveryJobIdsRef.current = [];
      }
      return;
    }
    pollAuditStatus();
  }, [clientId, hubEmbed, showXmlRecoveryUi, pollAuditStatus]);

  useEffect(() => {
    if (recoveryPhase !== "in_progress" || !clientId || hubEmbed || !showXmlRecoveryUi) return undefined;

    let cancelled = false;

    const checkRecoveryComplete = async () => {
      if (cancelled) return;
      const audit = await pollAuditStatus();
      if (cancelled) return;

      let syncing = Boolean(stats?.sync_in_progress || dashboard?.sync_in_progress);
      try {
        const { data: statsData } = await api.get("/sat/cfdi/stats", { params: { client_id: clientId } });
        syncing = Boolean(statsData?.sync_in_progress);
        if (statsData?.xml_integrity) {
          setXmlIntegrity(statsData.xml_integrity);
        }
      } catch {
        /* ignore poll errors */
      }

      const xi = audit?.xml_integrity || xmlIntegrity || {};
      const healing = Number(xi.healing) || 0;
      const missing = Number(xi.missing) || 0;
      const jobIds = recoveryJobIdsRef.current;
      const jobsStillRunning = jobIds.some((jobId) => satJobs?.getJob?.(jobId)?.status === "running");

      if (syncing || healing > 0 || (jobIds.length > 0 && jobsStillRunning)) {
        return;
      }

      try {
        const data = await pollAuditStatus();
        if (data) {
          applyXmlRecoveryResult(data);
        }
        loadStats();
        loadDashboard();
        loadList();
      } catch {
        /* final status poll is best-effort */
      }

      recoveryJobIdsRef.current = [];
      if (!cancelled) {
        finishRecoverySuccess();
      }
    };

    checkRecoveryComplete();
    const id = window.setInterval(checkRecoveryComplete, 4000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [
    recoveryPhase,
    clientId,
    hubEmbed,
    showXmlRecoveryUi,
    pollAuditStatus,
    stats?.sync_in_progress,
    dashboard?.sync_in_progress,
    xmlIntegrity,
    satJobs,
    finishRecoverySuccess,
    applyXmlRecoveryResult,
    loadStats,
    loadDashboard,
    loadList,
  ]);

  useEffect(() => {
    if (!hubEmbed) return undefined;
    const timer = setTimeout(() => {
      setAppliedFilters({
        searchTerm,
        fechaDesde,
        fechaHasta,
        tipo,
        estatus,
        direccion,
      });
      setPage(1);
    }, 320);
    return () => clearTimeout(timer);
  }, [hubEmbed, searchTerm, fechaDesde, fechaHasta, tipo, estatus, direccion]);

  useEffect(() => {
    if (!hubEmbed) return undefined;
    const onPeriod = (ev) => {
      const d = ev?.detail;
      if (!d?.fechaDesde || !d?.fechaHasta) return;
      setFechaDesde(d.fechaDesde);
      setFechaHasta(d.fechaHasta);
      setPeriodHint(d.label ? `Periodo: ${d.label}` : "");
    };
    window.addEventListener(VISOR_HUB_PERIOD_EVENT, onPeriod);
    return () => window.removeEventListener(VISOR_HUB_PERIOD_EVENT, onPeriod);
  }, [hubEmbed]);

  useEffect(() => {
    if (!clientId || !integrityRunning) return undefined;
    const id = setInterval(() => {
      pollAuditStatus().then((data) => {
        if (data?.status === "complete") {
          refreshAll();
        }
      });
    }, 4000);
    return () => clearInterval(id);
  }, [clientId, integrityRunning, pollAuditStatus]);

  const onDownloadXml = async (row) => {
    if (!clientId || !row?.id) return;
    setBusyXml(row.id);
    try {
      const { blob, source, generated, resolvedPath } = await fetchAccountingCfdiXmlBlob(
        clientId,
        row.id,
      );
      const stub = (row.uuid || row.id || "cfdi").slice(0, 36);
      const suffix = generated ? "_referencia" : "";
      downloadBlob(blob, `${stub}${suffix}.xml`);
      if (generated) {
        toast.warning(
          "XML de referencia generado desde la base de datos (no es el timbrado del SAT). Para el XML oficial, vuelva a descargar desde SAT o suba el archivo.",
          { duration: 8000 },
        );
      } else {
        const detail = resolvedPath ? ` (${resolvedPath})` : source ? ` [${source}]` : "";
        toast.success(`XML descargado${detail}`);
      }
    } catch (err) {
      if (err?.isHealing || err?.status === 202) {
        toast.info(
          err?.message ||
            "No hay XML en disco; se inició recuperación desde el SAT. Verifique integridad en unos minutos.",
          { duration: 9000 },
        );
        pollAuditStatus();
        return;
      }
      const detail = err?.detail ?? err?.response?.data?.detail;
      const audit = detail && typeof detail === "object" ? detail.audit : undefined;
      let msg = formatApiError(err, "No se pudo descargar el XML");
      if (audit?.resolved_absolute_path || audit?.bnt_storage_root) {
        msg += ` Búsqueda: ${audit.bnt_storage_root || ""}`;
        if (audit.resolved_absolute_path) {
          msg += ` · ruta Mongo: ${audit.resolved_absolute_path} (existe: ${audit.file_exists ? "sí" : "no"})`;
        }
      }
      toast.error(msg, { duration: 10000 });
    } finally {
      setBusyXml("");
    }
  };

  const onDownloadPdf = async (row) => {
    if (!row?.id) return;
    setBusyPdf(row.id);
    try {
      const blob = await fetchCfdiPdfBlob(row.id);
      const stub = (row.uuid || row.id || "cfdi").slice(0, 36);
      downloadBlob(blob, `CFDI_${stub}.pdf`);
      toast.success("PDF generado");
    } catch (err) {
      toast.error(formatApiError(err, "No se pudo generar el PDF"));
    } finally {
      setBusyPdf("");
    }
  };

  const onCompletePeriod = async (gap) => {
    if (!clientId || !gap?.fecha_inicio) return;
    setCompletingMonth(gap.month);
    try {
      const fd = new FormData();
      fd.append("client_id", clientId);
      fd.append("fecha_inicio", gap.fecha_inicio);
      fd.append("fecha_fin", gap.fecha_fin);
      const { data } = await api.post("/sat/descarga-automatica/iniciar", fd);
      toast.success(data?.message || "Descarga histórica encolada en segundo plano");
      setTimeout(refreshAll, 3000);
    } catch (err) {
      toast.error(formatApiError(err, "No se pudo iniciar la descarga del periodo"));
    } finally {
      setCompletingMonth("");
    }
  };

  const displayTotal = stats?.total_cfdis ?? dashboard?.total_cfdis ?? meta.total ?? 0;
  const syncingHistoric = Boolean(stats?.sync_in_progress || dashboard?.sync_in_progress);

  const pendingIntegrateCount = useMemo(
    () =>
      computePendingIntegrateCount(integritySummary, stats, {
        xml_pending_integration: xmlIntegrity?.pending_integration,
      }),
    [integritySummary, stats, xmlIntegrity],
  );
  const recoveryFailedCount =
    Number(xmlIntegrity?.recovery_failed) || Number(integritySummary?.recovery_failed) || 0;
  const xmlToRecoverCount = Math.max(0, Number(xmlIntegrity?.pending_integration ?? pendingIntegrateCount) || 0);
  const hasXmlToRecover = xmlToRecoverCount > 0;
  const missingMonthsToShow = hasXmlToRecover ? xmlRecovery?.missing_months || [] : [];
  const xmlRecoveryProbePending = hasXmlToRecover && !xmlRecovery && reconcileBusy;
  const designerSatQueueMessage = useMemo(
    () =>
      showTechnicalSyncUi
        ? describeDesignerSatQueueMessage(activeClient, xmlRecovery)
        : null,
    [showTechnicalSyncUi, activeClient, xmlRecovery],
  );
  const syncAutoActive = Boolean(
    activeClient?.modulo_descarga_activo || xmlRecovery?.sync_auto_active,
  );
  const syncEstado = (activeClient?.estado_sincronizacion || "").trim().toLowerCase();
  const satRobotBusy =
    syncAutoActive &&
    (syncingHistoric || syncEstado === "pendiente" || syncEstado === "procesando");
  const xmlRecoveryInProgress =
    satRobotBusy ||
    Boolean(xmlRecovery?.auto_healing_queued || xmlRecovery?.healing_started) ||
    recoveryPhase === "in_progress" ||
    reconcileBusy;
  const missingMonthsPhrase = formatMissingMonthsPhrase(missingMonthsToShow);
  const lastIntegrationAt = stats?.data_updated_at || dashboard?.data_updated_at || null;
  const showXmlDbUpToDateBanner =
    !hubEmbed &&
    clientId &&
    !hasXmlToRecover &&
    !integrityRunning &&
    !syncingHistoric &&
    recoveryFailedCount === 0;
  const showXmlRecoveryPanel = showXmlRecoveryUi && clientId && hasXmlToRecover;
  const showRecoverDbButton = showXmlRecoveryPanel && !xmlRecoveryInProgress;
  const recoveryActionsDisabled =
    !hasXmlToRecover || reconcileBusy || integrityRunning || recoveryPhase === "in_progress";
  const integrationInProgress =
    !hubEmbed && (integrityRunning || syncingHistoric || pendingIntegrateCount > 0);
  const showFriendlyIntegrationBanner =
    !hubEmbed && showXmlRecoveryUi && !showTechnicalSyncUi && integrationInProgress && !integrityRunning;
  const satActivityToday =
    xmlRecovery?.sat_activity_today || dashboard?.sat_activity_today || null;

  const showFriendlyFailureBanner =
    !hubEmbed &&
    showXmlRecoveryUi &&
    !showTechnicalSyncUi &&
    !integrationInProgress &&
    recoveryFailedCount > 0;

  useEffect(() => {
    if (!hubEmbed) return;
    const now = new Date();
    const y = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const lastDay = new Date(y, now.getMonth() + 1, 0).getDate();
    setFechaDesde(`${y}-${mm}-01`);
    setFechaHasta(`${y}-${mm}-${String(lastDay).padStart(2, "0")}`);
  }, [hubEmbed]);

  return (
    <div
      className="space-y-4"
      data-testid={hubEmbed ? "accounting-viewer-hub" : "accounting-viewer"}
      data-layout-rev="accounting-export-first-20260608"
    >
      {hubEmbed && periodHint ? (
        <p className="text-xs text-sky-900 bg-sky-50 border border-sky-200 rounded-md px-3 py-2 m-0" data-testid="hub-consult-period-hint">
          {periodHint}
        </p>
      ) : null}

      {!hubEmbed ? (
        <div className="min-w-[220px] max-w-md">
          <Label className="text-xs">Cliente</Label>
          <Select value={clientId || ""} onValueChange={setClientId}>
            <SelectTrigger className="mt-1 rounded-sm" data-testid="accounting-client-select">
              <SelectValue placeholder="Seleccione cliente" />
            </SelectTrigger>
            <SelectContent>
              {clients.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.razon_social} ({c.rfc})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : null}

      {!hubEmbed && clientId ? (
        <AccountingZipExportPanel
          clientId={clientId}
          clientRfc={activeClient?.rfc}
          appliedFilters={appliedFilters}
          dashboard={dashboard}
          disabled={!clientId}
        />
      ) : null}

      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[220px] flex-1 max-w-md">
          <Label className="text-xs">Buscar</Label>
          <Input
            className="mt-1 rounded-sm"
            placeholder="RFC, razón social, UUID, folio…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            list={relatedRfcs.length ? "accounting-rfc-suggestions" : undefined}
            data-testid={hubEmbed ? "hub-cfdi-keyword" : "accounting-cfdi-search"}
          />
          {relatedRfcs.length > 0 ? (
            <datalist id="accounting-rfc-suggestions">
              {relatedRfcs.map((r) => (
                <option key={r.rfc} value={r.rfc}>
                  {r.label}
                </option>
              ))}
            </datalist>
          ) : null}
        </div>
        <div>
          <Label className="text-xs">Desde</Label>
          <Input
            type="date"
            className="mt-1 rounded-sm w-36"
            value={fechaDesde}
            onChange={(e) => setFechaDesde(e.target.value)}
          />
        </div>
        <div>
          <Label className="text-xs">Hasta</Label>
          <Input
            type="date"
            className="mt-1 rounded-sm w-36"
            value={fechaHasta}
            onChange={(e) => setFechaHasta(e.target.value)}
          />
        </div>
        <div>
          <Label className="text-xs">Tipo</Label>
          <Select value={tipo} onValueChange={setTipo}>
            <SelectTrigger className="mt-1 rounded-sm w-28">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los tipos</SelectItem>
              <SelectItem value="I">Ingreso</SelectItem>
              <SelectItem value="E">Egreso</SelectItem>
              <SelectItem value="N">Nómina</SelectItem>
              <SelectItem value="P">Pago</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Estatus</Label>
          <Select value={estatus} onValueChange={setEstatus}>
            <SelectTrigger className="mt-1 rounded-sm w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos</SelectItem>
              <SelectItem value="vigente">Vigente</SelectItem>
              <SelectItem value="cancelado">Cancelado</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Dirección</Label>
          <Select value={direccion} onValueChange={setDireccion}>
            <SelectTrigger className="mt-1 rounded-sm w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              <SelectItem value="emitido">Emitido</SelectItem>
              <SelectItem value="recibido">Recibido</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {!hubEmbed ? (
          <Button type="button" variant="outline" className="rounded-sm" onClick={applyFilters}>
            Filtrar
          </Button>
        ) : null}
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={refreshAll}
          disabled={loading || statsLoading || dashboardLoading}
          title="Actualizar"
        >
          <RefreshCw
            className={`h-4 w-4 ${loading || statsLoading || dashboardLoading ? "animate-spin" : ""}`}
          />
        </Button>
      </div>

      {!clientId ? (
        <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
          {hubEmbed
            ? "Seleccione un cliente en el panel superior del hub para consultar sus comprobantes."
            : "Seleccione un cliente para consultar su inventario de CFDI (solo datos de su espacio de trabajo)."}
        </p>
      ) : (
        <>
          {!hubEmbed && (statsLoading || dashboardLoading) && !stats ? (
            <div className="flex justify-center py-6">
              <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
            </div>
          ) : null}

          {!hubEmbed && integrityRunning ? (
            <div
              className="rounded-md border border-indigo-200 bg-indigo-50 px-4 py-3 flex items-center gap-2 animate-pulse"
              data-testid="accounting-integrity-running"
            >
              <Loader2 className="h-5 w-5 text-indigo-700 animate-spin shrink-0" />
              <p className="text-sm font-semibold text-indigo-950 m-0">
                {integrityMessage || "Verificando integridad…"}
              </p>
            </div>
          ) : null}

          {!hubEmbed && showXmlRecoveryUi && recoveryPhase !== "idle" ? (
            <div
              className={`rounded-md border px-4 py-3 flex items-center gap-2 ${
                recoveryPhase === "success"
                  ? "border-emerald-200 bg-emerald-50"
                  : "border-indigo-200 bg-indigo-50 animate-pulse"
              }`}
              data-testid="accounting-xml-recovery-status"
            >
              {recoveryPhase === "success" ? (
                <CheckCircle2 className="h-5 w-5 text-emerald-700 shrink-0" aria-hidden />
              ) : (
                <Loader2 className="h-5 w-5 text-indigo-700 animate-spin shrink-0" aria-hidden />
              )}
              <p
                className={`text-sm font-semibold m-0 ${
                  recoveryPhase === "success" ? "text-emerald-950" : "text-indigo-950"
                }`}
              >
                {recoveryPhase === "success" ? "Proceso terminado con éxito" : "En proceso de recuperación"}
              </p>
            </div>
          ) : null}

          {!hubEmbed ? (
            <CredentialAuthBanner credentialHealth={dashboard?.credential_health} />
          ) : null}

          {!hubEmbed ? (
            <IntegrityBanner
              dashboard={dashboard}
              xmlPendingCount={showXmlRecoveryUi ? xmlToRecoverCount : 0}
            />
          ) : null}

          {showFriendlyIntegrationBanner ? (
            <div
              className="rounded-md border border-sky-200 bg-sky-50 px-4 py-3 space-y-2"
              data-testid="accounting-integration-status-friendly"
            >
              <div className="flex items-start gap-2">
                <CloudDownload className="h-5 w-5 text-sky-700 shrink-0 mt-0.5" aria-hidden />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-sky-950 m-0">
                    {syncingHistoric
                      ? stats?.sync_message || "Sincronizando histórico con el SAT…"
                      : "Integración de comprobantes en curso"}
                  </p>
                  {pendingIntegrateCount > 0 ? (
                    <p className="text-sm text-sky-900 m-0 mt-1">
                      <strong className="tabular-nums">{pendingIntegrateCount.toLocaleString("es-MX")}</strong>{" "}
                      comprobante{pendingIntegrateCount === 1 ? "" : "s"} pendiente
                      {pendingIntegrateCount === 1 ? "" : "s"} de completar en su copia local. El sistema los está
                      recuperando en segundo plano; puede seguir consultando la tabla.
                    </p>
                  ) : (
                    <p className="text-sm text-sky-900 m-0 mt-1">
                      Validación e integración con el SAT en proceso. Puede tardar según el volumen histórico cargado.
                    </p>
                  )}
                </div>
              </div>
            </div>
          ) : null}

          {showFriendlyFailureBanner ? (
            <div
              className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 space-y-2"
              data-testid="accounting-integration-failure-friendly"
            >
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-800 shrink-0 mt-0.5" aria-hidden />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-amber-950 m-0">
                    No se pudieron integrar {recoveryFailedCount.toLocaleString("es-MX")} comprobante
                    {recoveryFailedCount === 1 ? "" : "s"}
                  </p>
                  <p className="text-sm text-amber-900 m-0 mt-1">
                    Revise la FIEL del cliente o use la descarga manual desde el SAT. No es un fallo general del
                    sistema.
                  </p>
                  <Button type="button" size="sm" variant="outline" className="mt-2 h-8 rounded-sm" asChild>
                    <Link to="/descarga-automatica-sat">Ir a descarga SAT</Link>
                  </Button>
                </div>
              </div>
            </div>
          ) : null}

          {showXmlDbUpToDateBanner && dashboard?.integrity_status !== "complete" ? (
            <div
              className="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 flex items-start gap-2"
              data-testid="accounting-xml-db-up-to-date"
            >
              <CheckCircle2 className="h-5 w-5 text-emerald-700 shrink-0 mt-0.5" aria-hidden />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-emerald-950 m-0">Base de datos al día</p>
                <p className="text-xs text-emerald-900 m-0 mt-1 leading-relaxed">
                  Todos los comprobantes tienen XML integrado en la base de datos.
                  {lastIntegrationAt ? (
                    <>
                      {" "}
                      Última integración:{" "}
                      <span className="font-medium tabular-nums">{formatFecha(lastIntegrationAt)}</span>.
                    </>
                  ) : null}{" "}
                  No es necesario pulsar recuperación manual.
                </p>
              </div>
            </div>
          ) : null}

          {showXmlRecoveryPanel ? (
            <div
              className={`rounded-md border px-4 py-3 space-y-2 ${
                xmlRecoveryInProgress
                  ? "border-sky-200 bg-sky-50"
                  : "border-amber-200 bg-amber-50"
              }`}
              data-testid="accounting-xml-recovery-panel"
            >
              {showRecoverDbButton ? (
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="default"
                    className="h-8 rounded-sm bg-[#0F2D5C] hover:bg-[#0F2D5C]/90"
                    disabled={recoveryActionsDisabled}
                    onClick={() => runReconcileXml({ triggerHealing: true })}
                    data-testid="accounting-reconcile-xml-db"
                  >
                    {reconcileBusy ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : (
                      <ShieldCheck className="h-3 w-3 mr-1" />
                    )}
                    Recuperar desde base de datos
                  </Button>
                </div>
              ) : null}
              {hasXmlToRecover ? (
                <div className="space-y-1.5" data-testid="accounting-xml-recovery-status-text">
                  {xmlRecoveryProbePending ? (
                    <p className="text-xs text-slate-700 m-0 flex items-center gap-1.5">
                      <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" aria-hidden />
                      Verificando periodos con XML pendiente…
                    </p>
                  ) : (
                    <>
                      <p
                        className={`text-sm font-semibold m-0 flex items-center gap-1.5 ${
                          xmlRecoveryInProgress ? "text-sky-950" : "text-amber-950"
                        }`}
                      >
                        {xmlRecoveryInProgress ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" aria-hidden />
                        ) : null}
                        {xmlRecoveryInProgress
                          ? "Recuperación de XML en curso"
                          : "XML pendiente de integrar"}
                      </p>
                      <p
                        className={`text-xs m-0 leading-relaxed ${
                          xmlRecoveryInProgress ? "text-sky-900" : "text-amber-900"
                        }`}
                      >
                        Hay{" "}
                        <strong className="tabular-nums">
                          {xmlToRecoverCount.toLocaleString("es-MX")} comprobante
                          {xmlToRecoverCount === 1 ? "" : "s"}
                        </strong>{" "}
                        con XML por recuperar.
                        {xmlRecoveryInProgress ? (
                          <> El sistema ya está en proceso de recuperación.</>
                        ) : (
                          <>
                            {" "}
                            Pulse <strong>Recuperar desde base de datos</strong> para iniciar la
                            búsqueda en su copia local.
                          </>
                        )}
                        {missingMonthsPhrase ? (
                          <>
                            {" "}
                            <span className="font-medium">Periodos:</span>{" "}
                            <strong>{missingMonthsPhrase}</strong>.
                          </>
                        ) : null}
                      </p>
                      {xmlRecoveryInProgress ? (
                        <p className="text-[11px] text-sky-800 m-0">
                          Puede seguir consultando la tabla; al terminar verá el aviso{" "}
                          <strong>«Base de datos al día»</strong>.
                        </p>
                      ) : !syncAutoActive ? (
                        <p className="text-[11px] text-amber-800 m-0">
                          También puede activar la sincronización automática SAT en Clientes.
                        </p>
                      ) : null}
                    </>
                  )}
                </div>
              ) : null}
              {showTechnicalSyncUi && (designerSatQueueMessage || satActivityToday) ? (
                <details className="text-[11px] text-slate-600" data-testid="accounting-xml-recovery-designer-queue">
                  <summary className="cursor-pointer select-none text-slate-500 hover:text-slate-700">
                    Detalle técnico (diseñador)
                  </summary>
                  {designerSatQueueMessage ? (
                    <p className="font-mono mt-1.5 m-0 leading-relaxed text-amber-950">
                      {designerSatQueueMessage}
                    </p>
                  ) : null}
                  {satActivityToday ? (
                    <p
                      className="font-mono mt-1.5 m-0 leading-relaxed text-slate-600 tabular-nums"
                      data-testid="accounting-sat-activity-today"
                    >
                      Actividad SAT hoy (UTC): {satActivityToday.jobs_started ?? 0} job(s),{" "}
                      {satActivityToday.sat_solicitudes_registered ?? 0} solicitud(es) SOAP, ~
                      {satActivityToday.estimated_soap_calls ?? 0} llamadas estimadas.
                    </p>
                  ) : null}
                </details>
              ) : null}
            </div>
          ) : null}

          {/* UI paso 1: matriz en visor SAT; el backend sigue calculando missing_months */}
          {false && dashboard?.missing_months?.length > 0 ? (
            <div
              className="rounded-md border border-amber-200 bg-amber-50/60 p-4 space-y-2"
              data-testid="accounting-missing-months"
            >
              <p className="text-sm font-semibold text-amber-950 m-0">
                Periodos faltantes en histórico ({dashboard.missing_months_total} meses)
              </p>
              <p className="text-xs text-amber-900 m-0">
                Puede completar cada mes con descarga SAT en segundo plano (hasta 5 años por solicitud
                según política SAT; rangos largos se encolan sin bloquear la interfaz).
              </p>
              <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                {dashboard.missing_months.slice(0, 24).map((gap) => (
                  <Button
                    key={gap.month}
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-8 rounded-sm text-xs border-amber-300 bg-white"
                    disabled={completingMonth === gap.month || dashboard.sync_in_progress}
                    onClick={() => onCompletePeriod(gap)}
                  >
                    {completingMonth === gap.month ? (
                      <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                    ) : (
                      <CloudDownload className="h-3 w-3 mr-1" />
                    )}
                    Completar {gap.label}
                  </Button>
                ))}
              </div>
              <Button type="button" variant="link" className="text-xs h-auto p-0" asChild>
                <Link to="/descarga-automatica-sat">Ir a Descarga de CFDI SAT →</Link>
              </Button>
            </div>
          ) : null}
        </>
      )}

      <div className="rounded-md border overflow-x-auto bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              {hubEmbed ? (
                <>
                  <TableHead className="font-mono text-xs">Folio</TableHead>
                  <TableHead className="font-mono text-xs">RFC Emisor</TableHead>
                  <TableHead className="font-mono text-xs">RFC Receptor</TableHead>
                  <TableHead>Fecha</TableHead>
                  <TableHead className="min-w-[180px]">Concepto</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Estatus</TableHead>
                  <TableHead className="text-right w-[120px]">Acciones</TableHead>
                </>
              ) : (
                <>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Emisor</TableHead>
                  <TableHead className="font-mono text-xs">RFC</TableHead>
                  <TableHead className="min-w-[200px]">Concepto</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead>Estatus</TableHead>
                  <TableHead className="w-10 text-center" title="Integridad del archivo">
                    Int.
                  </TableHead>
                  <TableHead>Fuente</TableHead>
                  <TableHead className="text-right w-[140px]">Acciones</TableHead>
                </>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {!clientId && (
              <TableRow>
                <TableCell colSpan={9} className="text-center text-slate-500 py-10">
                  Elija un cliente en el selector superior.
                </TableCell>
              </TableRow>
            )}
            {clientId && !loading && items.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} className="text-center text-slate-500 py-10">
                  No hay CFDI con los filtros actuales.
                </TableCell>
              </TableRow>
            )}
            {loading && (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-10">
                  <Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" />
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              items.map((row) => (
                <TableRow key={row.id} data-testid={`accounting-row-${row.uuid || row.id}`}>
                  {hubEmbed ? (
                    <>
                      <TableCell className="font-mono text-xs whitespace-nowrap">{folioDisplay(row)}</TableCell>
                      <TableCell className="font-mono text-xs">{row.rfc_emisor || "—"}</TableCell>
                      <TableCell className="font-mono text-xs">{row.rfc_receptor || "—"}</TableCell>
                      <TableCell className="text-xs whitespace-nowrap">{formatFecha(row.fecha)}</TableCell>
                      <TableCell className="text-xs max-w-[240px] truncate" title={row.concepto}>
                        {row.concepto || "—"}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-sm">
                        {fmtMoney(row.total)}
                      </TableCell>
                      <TableCell className="text-xs">{tipoComprobanteLabel(row.tipo_comprobante)}</TableCell>
                      <TableCell>
                        <EstadoBadge estado={row.estado} kind={row.estado_badge} />
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="h-8 rounded-sm text-xs px-2"
                            disabled={busyXml === row.id}
                            onClick={() => onDownloadXml(row)}
                            title="Descargar XML"
                          >
                            {busyXml === row.id ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <FileCode2 className="h-3.5 w-3.5" />
                            )}
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="h-8 rounded-sm text-xs px-2"
                            disabled={busyPdf === row.id}
                            onClick={() => onDownloadPdf(row)}
                            title="PDF"
                          >
                            {busyPdf === row.id ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <FileText className="h-3.5 w-3.5" />
                            )}
                          </Button>
                        </div>
                      </TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell className="text-xs whitespace-nowrap">{formatFecha(row.fecha)}</TableCell>
                      <TableCell className="max-w-[160px] truncate" title={row.nombre_emisor}>
                        {row.nombre_emisor || "—"}
                      </TableCell>
                      <TableCell className="font-mono text-xs">{row.rfc_emisor || "—"}</TableCell>
                      <TableCell className="text-xs max-w-[280px] truncate" title={row.concepto}>
                        {row.concepto || "—"}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-sm">
                        {fmtMoney(row.total)}
                      </TableCell>
                      <TableCell>
                        <EstadoBadge estado={row.estado} kind={row.estado_badge} />
                      </TableCell>
                      <TableCell className="text-center">
                        <IntegrityStatusIcon
                          status={row.integrity_status}
                          title={
                            row.last_verified_at
                              ? `Última verificación: ${row.last_verified_at}`
                              : undefined
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <FuenteBadge label={row.ingestion_source_label} code={row.ingestion_source} />
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="h-8 rounded-sm text-xs"
                            disabled={busyXml === row.id}
                            onClick={() => onDownloadXml(row)}
                            title={
                              row.has_stored_xml
                                ? "Descargar XML timbrado almacenado"
                                : "Descargar XML (referencia desde BD si no hay archivo en disco)"
                            }
                          >
                            {busyXml === row.id ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <FileCode2 className="h-3.5 w-3.5 mr-1" />
                            )}
                            XML
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="h-8 rounded-sm text-xs"
                            disabled={busyPdf === row.id}
                            onClick={() => onDownloadPdf(row)}
                            title="Generar o descargar PDF"
                          >
                            {busyPdf === row.id ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <FileText className="h-3.5 w-3.5 mr-1" />
                            )}
                            PDF
                          </Button>
                        </div>
                      </TableCell>
                    </>
                  )}
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>

      {!hubEmbed && clientId ? (
        <details
          className="rounded-md border border-slate-200 bg-white group"
          data-testid="accounting-secondary-details"
        >
          <summary className="px-4 py-3 text-sm font-medium text-[#0F2D5C] cursor-pointer select-none list-none marker:content-none flex items-center gap-2">
            <ChevronRight className="h-4 w-4 text-slate-500 transition-transform group-open:rotate-90" aria-hidden />
            Más información y carga histórica
          </summary>
          <div className="px-4 pb-4 pt-0 space-y-4 border-t border-slate-100">
            <div>
              <p className="text-sm font-semibold text-[#0F2D5C] m-0 mb-3">Resumen de carga histórica</p>
              <HistoricalLoadSummary stats={stats} />
              {stats?.unsynced_years?.length > 0 && !syncingHistoric ? (
                <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1.5 mt-3 m-0">
                  Años sin CFDI ({stats.window_scope_label || `últimos ${stats.historical_years ?? 5} años`}):{" "}
                  <strong>{stats.unsynced_years.join(", ")}</strong>. Use descarga SAT manual o active la
                  sincronización automática desde la fecha configurada en el cliente.
                </p>
              ) : null}
            </div>

            {dashboard?.fiscal_open_gaps > 0 && !dashboard?.credential_health?.downloads_paused ? (
              <p
                className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 m-0"
                data-testid="accounting-fiscal-gaps-hint"
              >
                Periodos históricos pendientes en cola de recuperación:{" "}
                <strong>{dashboard.fiscal_open_gaps}</strong> (prioridad alta antes del modo delta).
              </p>
            ) : null}

            {showTechnicalSyncUi && syncingHistoric ? (
              <div
                className="rounded-md border border-sky-300 bg-sky-50 px-4 py-3 flex items-center gap-2 animate-pulse"
                data-testid="accounting-sync-historic-banner"
              >
                <CloudDownload className="h-5 w-5 text-sky-700 shrink-0" />
                <p className="text-sm font-semibold text-sky-900 m-0">
                  {stats?.sync_message || "Sincronizando histórico..."}
                </p>
              </div>
            ) : null}

            {showTechnicalSyncUi && integritySummary && !integrityRunning ? (
              <div
                className="rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-700"
                data-testid="accounting-integrity-summary"
              >
                <p className="m-0 font-semibold text-slate-800">Última auditoría de archivos</p>
                <p className="m-0 mt-1 tabular-nums">
                  OK: {integritySummary.ok ?? 0} · Re-enlazados: {integritySummary.relinked ?? 0} ·
                  Faltantes: {integritySummary.missing ?? 0} · En recuperación:{" "}
                  {integritySummary.healing ?? 0} · Fallas: {integritySummary.recovery_failed ?? 0}
                </p>
                <div className="flex flex-wrap gap-2 mt-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-8 rounded-sm"
                    disabled={auditBusy || integrityRunning}
                    onClick={() => runStorageAudit(true)}
                  >
                    {auditBusy ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : null}
                    Auditoría completa del cliente
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="default"
                    className="h-8 rounded-sm bg-[#0F2D5C] hover:bg-[#0F2D5C]/90"
                    disabled={forceAuditBusy || integrityRunning}
                    onClick={() => runForceHealthAudit(false)}
                    data-testid="accounting-force-health-audit"
                  >
                    {forceAuditBusy ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : (
                      <ShieldCheck className="h-3 w-3 mr-1" />
                    )}
                    Forzar auditoría
                  </Button>
                </div>
              </div>
            ) : null}

            {showTechnicalSyncUi && recoveryLogs.length > 0 && recoveryPhase !== "success" ? (
              <div
                className="rounded-md border border-amber-200 bg-amber-50/80 p-3 max-h-36 overflow-y-auto"
                data-testid="accounting-recovery-logs"
              >
                <p className="text-xs font-semibold text-amber-950 m-0 mb-2">
                  Bitácora técnica de recuperación / auditoría
                </p>
                <ul className="text-[11px] text-amber-900 space-y-1 m-0 pl-4 list-disc">
                  {recoveryLogs.slice(0, 12).map((log) => (
                    <li key={log.id}>
                      <span className="font-mono">{log.uuid?.slice(0, 8) || "—"}</span> — {log.status}:{" "}
                      {log.message}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        </details>
      ) : null}

      {clientId && meta.total > 0 ? (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <span>Filas por página</span>
            <Select
              value={String(pageSize)}
              onValueChange={(v) => {
                setPageSize(Number(v));
                setPage(1);
              }}
            >
              <SelectTrigger className="h-8 w-20 rounded-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAGE_SIZES.map((n) => (
                  <SelectItem key={n} value={String(n)}>
                    {n}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="tabular-nums">
              {meta.skip + 1}–{Math.min(meta.skip + meta.limit, meta.total)} de{" "}
              {meta.total.toLocaleString("es-MX")}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="rounded-sm"
              disabled={page <= 1 || loading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              <ChevronLeft className="h-4 w-4" />
              Anterior
            </Button>
            <span className="text-sm tabular-nums px-2">
              {meta.page} / {meta.total_pages}
            </span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="rounded-sm"
              disabled={page >= meta.total_pages || loading}
              onClick={() => setPage((p) => p + 1)}
            >
              Siguiente
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
